import { type PropsWithChildren, useCallback, useMemo, useState } from 'react';
import immer from 'immer';
import { noop } from 'lodash';
import { createContext, useContextSelector } from 'use-context-selector';

/**
 * @description the `BulkOperationsMoveAssetsState` tracks the state of assets being moved as part of an in progres Bulk Move Asset Operation.
 *
 * @property {Set<string>} assets - a Set of asset IDs being moved
 */
type BulkOperationsMoveAssetsState = {
  assets: Set<string>;
};

type BulkOperationsMoveAssetsValue = {
  addMovingAssets: (assetIds: string[]) => void;
  removeMovingAssets: (assetIds: string[]) => void;
  isAssetMoving: (assetId?: string) => boolean;
  hasAssetsRemaining: () => boolean;
  clearAssets: () => void;
};

const initialState: BulkOperationsMoveAssetsState = {
  assets: new Set(),
};

const BulkOperationsMoveAssetsContext =
  createContext<BulkOperationsMoveAssetsValue>({
    addMovingAssets: noop,
    removeMovingAssets: noop,
    isAssetMoving: noop as unknown as (assetId?: string) => boolean,
    hasAssetsRemaining: noop as unknown as () => boolean,
    clearAssets: noop,
  });
/**
 * The BulkOperationsMoveAssetsContextProvider tracks the state of assets being moved as part of an in-progress bulk move assets operation
 * triggered by the current user, exposing methods for adding assets to the set, removing assets from the set, and reading whether an asset is part of the set.
 */
export function BulkOperationsMoveAssetsContextProvider({
  children,
}: PropsWithChildren) {
  const [state, setState] =
    useState<BulkOperationsMoveAssetsState>(initialState);

  const addMovingAssets = useCallback(
    (assetIds: string[]) => {
      setState(
        immer((draftValue) => {
          assetIds.forEach((assetId) => {
            draftValue.assets.add(assetId);
          });
        })
      );
    },
    [setState]
  );

  const removeMovingAssets = useCallback(
    (assetIds: string[]) => {
      setState(
        immer((draftValue) => {
          assetIds.forEach((assetId) => {
            draftValue.assets.delete(assetId);
          });
        })
      );
    },
    [setState]
  );

  const isAssetMoving = useCallback(
    (assetId?: string) => {
      if (!assetId) return false;

      return state.assets.has(assetId);
    },
    [state.assets]
  );

  const hasAssetsRemaining = useCallback(() => {
    return state.assets.size > 0;
  }, [state.assets]);

  const clearAssets = useCallback(() => {
    setState(initialState);
  }, [setState]);

  const contextValue = useMemo<BulkOperationsMoveAssetsValue>(
    () => ({
      addMovingAssets,
      removeMovingAssets,
      isAssetMoving,
      hasAssetsRemaining,
      clearAssets,
    }),
    [
      addMovingAssets,
      removeMovingAssets,
      isAssetMoving,
      hasAssetsRemaining,
      clearAssets,
    ]
  );

  return (
    <BulkOperationsMoveAssetsContext.Provider value={contextValue}>
      <>{children}</>
    </BulkOperationsMoveAssetsContext.Provider>
  );
}

export function useAddMovingAssets() {
  return useContextSelector(
    BulkOperationsMoveAssetsContext,
    (value) => value.addMovingAssets
  );
}

export function useRemoveMovingAssets() {
  return useContextSelector(
    BulkOperationsMoveAssetsContext,
    (value) => value.removeMovingAssets
  );
}

export function useIsAssetMoving(assetId?: string) {
  return useContextSelector(BulkOperationsMoveAssetsContext, (value) =>
    value.isAssetMoving(assetId)
  );
}

export function useHasMovingAssetsRemaining() {
  return useContextSelector(
    BulkOperationsMoveAssetsContext,
    (value) => value.hasAssetsRemaining
  );
}

export function useClearAllAssets() {
  return useContextSelector(
    BulkOperationsMoveAssetsContext,
    (value) => value.clearAssets
  );
}
